import { Check, Package } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Task #744 — shared variant picker. Lets users pick ONE product variant (or
// "No specific variant") wherever ads are generated. The selected variant's
// picture becomes the primary product reference and its description overrides
// the product-level value on the server. Picking "No specific variant"
// keeps the original no-variant generation path byte-for-byte unchanged.

export interface PickerVariant {
  name: string;
  price?: string;
  imageUrl?: string;
  description?: string;
}

const NO_VARIANT = "__none__";

export function getProductVariants(product: unknown): PickerVariant[] {
  const raw = (product as { variantsJson?: unknown } | null | undefined)?.variantsJson;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((v): v is PickerVariant => !!v && typeof (v as PickerVariant).name === "string" && (v as PickerVariant).name.trim() !== "");
}

export function VariantPicker({
  product,
  value,
  onChange,
  disabled = false,
  label = "Variant",
  className,
  testIdPrefix = "variant",
}: {
  product: unknown;
  value: string | null;
  onChange: (variantName: string | null) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
  testIdPrefix?: string;
}) {
  const variants = getProductVariants(product);
  // No variants defined → render nothing so surfaces stay identical to before.
  if (variants.length === 0) return null;

  const tileClass = (selected: boolean) =>
    cn(
      "relative flex items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-left text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:border-primary/40 hover:bg-muted/40",
    );

  return (
    <div className={cn("space-y-2", className)}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2">
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          aria-pressed={value == null}
          className={tileClass(value == null)}
          data-testid={`button-${testIdPrefix}-none`}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Package className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="min-w-0 flex-1 truncate font-medium">No specific variant</span>
          {value == null && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </button>
        {variants.map((variant, i) => {
          const selected = value === variant.name;
          return (
            <button
              key={`${variant.name}-${i}`}
              type="button"
              onClick={() => onChange(variant.name)}
              disabled={disabled}
              aria-pressed={selected}
              className={tileClass(selected)}
              data-testid={`button-${testIdPrefix}-${i}`}
            >
              {variant.imageUrl ? (
                <img
                  src={variant.imageUrl}
                  alt={variant.name}
                  className="h-9 w-9 shrink-0 rounded-lg object-cover"
                />
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <span className="min-w-0 flex-1 truncate font-medium">{variant.name}</span>
              {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { NO_VARIANT };
